// Tiny pub/sub for cross-panel composer actions.
// Avoids prop-drilling through Composer.tsx (1759 lines).
//
// Events:
//   - chat:insert  → ChatRail populate-and-focus (never auto-send)
//   - draft:insert → DraftCanvas replace-clean (auto-stash dirty canvas as Draft first)
//                    or append-at-cursor (Blocks only)

import type { BriefDocument } from "@/lib/briefDocument";

export type ChatInsertPayload = {
  text: string;
  source?: string; // e.g. "prompt:abc123" — for telemetry/labeling
  /**
   * When true, the bridge auto-submits the message after populating the
   * textarea. Used by Prompts "Push & Run". If the textarea already has
   * unsent text, the bridge confirms before overwriting + sending.
   */
  autoSend?: boolean;
  /**
   * Suggested chat model id, surfaced by `composer_excerptus_prompts`/
   * `composer_user_prompts.model_hint`. Currently advisory only — there is
   * no chat-side model picker to override. Reserved for future use.
   */
  modelHint?: string | null;
};

export type DraftInsertPayload = {
  doc: BriefDocument | unknown; // TipTap JSON
  /**
   * - "replace-clean": clear the canvas (auto-stash if dirty) and load this doc.
   *   Used by Templates and Blueprints.
   * - "append-at-cursor": insert at the current cursor without touching existing content.
   *   Used by Blocks only.
   */
  mode: "replace-clean" | "append-at-cursor";
  source?: string; // e.g. "template:abc123"
  label?: string; // human-readable, used for the auto-stash Draft label
};

export type PieceOpenPayload = {
  pieceId: string;
  label?: string;
};

export type PieceOpenAckPayload = {
  pieceId: string;
  stashed: boolean;
};

export type RailMode = "planning" | "chat";

export type RailSetModePayload = {
  mode: RailMode;
};

/**
 * Per-token streaming push from AI Chat into the Draft Canvas.
 * - `start`: lock the canvas, prepare a target revision to receive the stream.
 *   `mode` controls how the stream lands (default "new"):
 *     - "new"     → create a fresh user_edit revision (leaves current draft intact).
 *     - "append"  → continue writing into the current active draft, appending
 *                   the parsed content after the existing doc.
 *     - "replace" → overwrite the current active draft's content in place.
 * - `chunk`: cumulative markdown buffer (not delta). Subscribers re-run
 *   markdown→TipTap and replace the editor content without emitting updates.
 * - `end`: flush + save final doc to the target, unlock canvas.
 */
export type DraftStreamMode = "new" | "append" | "replace";
export type DraftStreamPayload =
  | { phase: "start"; source?: string; label?: string; mode?: DraftStreamMode }
  | { phase: "chunk"; markdown: string }
  | { phase: "end"; markdown: string; aborted?: boolean };

/**
 * Per-token streaming push from the brief draft pass (`runBriefDraftPass`).
 * Unlike `draft:stream`, the draft row is already created server-side, so
 * subscribers should NOT create new Draft revisions — just render incoming
 * markdown into the editor when `briefId` matches the open canvas piece.
 */
export type BriefDraftStreamPayload =
  | { phase: "start"; briefId: string; label?: string }
  | { phase: "chunk"; briefId: string; markdown: string }
  | { phase: "end"; briefId: string; markdown: string; aborted?: boolean };

/**
 * Per-token streaming push from the publication prep pass
 * (`runBriefPublicationPrep`). The publication row is created server-side
 * before streaming begins; subscribers receive cumulative `prepared_body`
 * markdown for live preview and a final saved publication on `end`.
 */
export type PublicationStreamPayload =
  | { phase: "start"; briefId: string; draftId: string; destinationFormat: string; label?: string }
  | { phase: "chunk"; briefId: string; draftId: string; destinationFormat: string; markdown: string }
  | { phase: "end"; briefId: string; draftId: string; destinationFormat: string; markdown: string; aborted?: boolean };

// MicroPost stream payload removed 2026-07-03 (MicroPost retirement).

/**
 * Pre-seeded first user turn for the Planning Agent conversation on the
 * active Piece. Emitted when a Dynamic template's wizard intake is
 * submitted. Subscriber (BriefIntakeChat) auto-sends `text` as the first
 * user message, but only when the planning transcript is empty — if there
 * is already an in-flight conversation it surfaces a toast asking the user
 * to reset it first.
 */
export type PlanningSeedPayload = {
  pieceId: string;
  text: string;
  source?: string; // e.g. "template:abc123"
  templateId?: string;
  templateName?: string;
};

export type PieceDraftFocusPayload = {
  pieceId: string;
  draftId: string;
};

type EventMap = {
  "chat:insert": ChatInsertPayload;
  "draft:insert": DraftInsertPayload;
  "draft:stream": DraftStreamPayload;
  "brief-draft:stream": BriefDraftStreamPayload;
  "publication:stream": PublicationStreamPayload;
  
  "piece:open": PieceOpenPayload;
  "piece:open:ack": PieceOpenAckPayload;
  "piece:draft:focus": PieceDraftFocusPayload;
  "rail:setMode": RailSetModePayload;
  "planning:seed": PlanningSeedPayload;
};

type Listener<E extends keyof EventMap> = (payload: EventMap[E]) => void;

const listeners: { [E in keyof EventMap]: Set<Listener<E>> } = {
  "chat:insert": new Set(),
  "draft:insert": new Set(),
  "draft:stream": new Set(),
  "brief-draft:stream": new Set(),
  "publication:stream": new Set(),
  
  "piece:open": new Set(),
  "piece:open:ack": new Set(),
  "piece:draft:focus": new Set(),
  "rail:setMode": new Set(),
  "planning:seed": new Set(),
};





export function subscribe<E extends keyof EventMap>(event: E, fn: Listener<E>): () => void {
  listeners[event].add(fn as Listener<keyof EventMap>);
  return () => {
    listeners[event].delete(fn as Listener<keyof EventMap>);
  };
}

export function emit<E extends keyof EventMap>(event: E, payload: EventMap[E]): void {
  for (const fn of listeners[event]) {
    try {
      (fn as Listener<E>)(payload);
    } catch (err) {
      // Never let one subscriber break the others.
      console.error(`composerBus listener for ${event} threw`, err);
    }
  }
}

export const composerBus = { subscribe, emit };
